import { createSelector, createSlice } from '@reduxjs/toolkit'

const initalState = {
  callSlots: [
    { caller: 'Empty slot 1', participants: [], isOnHold: false },
    { caller: 'Empty slot 2', participants: [], isOnHold: false },
    { caller: 'Empty slot 3', participants: [], isOnHold: false },
    { caller: 'Empty slot 4', participants: [], isOnHold: false },
  ],
  isInCall: false,
  currentCall: null,
  isConference: false,
  isInVideoCall: false,
  isOnHold: false,
  // local hold (mic muted by user via Hold button), independent of backend conference hold
  isLocalHold: false,
  // epoch ms when the remote answered; lets the InCall timer survive leaving/re-entering /call
  callStartedAt: null,
  // Local Twilio Call objects held by initTwilioVoice (iOS CallManager.calls model).
  // Separate from `callSlots`, which mirrors the backend conference lines.
  localCalls: [], // { id, callSid, incomingCallSid, caller, callerId, organizationName, direction, isOnHold, startedAt }
  activeLocalCallId: null,
}

const callsSlice = createSlice({
  name: 'calls',
  initialState: initalState,
  reducers: {
    setCallsOnHold: (state, action) => {
      state.callSlots = state.callSlots?.map((call) => {
        if (call?.callerId === action?.payload?.callerId) {
          return {
            ...call,
            isOnHold: true,
          }
        } else {
          return call
        }
      })
    },
    removeCallOnHold: (state, action) => {
      state.callSlots = state.callSlots?.map((call) => {
        if (call?.callerId === action?.payload?.callerId) {
          return {
            ...call,
            isOnHold: false,
          }
        } else {
          return call
        }
      })
    },
    setIsInCall: (state, action) => {
      state.isInCall = action.payload
    },
    setCurrentCall: (state, action) => {
      state.currentCall = {
        ...action.payload,
        isOnHold: false,
      }
    },
    resetCall: () => {
      return initalState
    },
    setCallSlot: (state, action) => {
      const index = state.callSlots.findIndex(
        (call) => call?.callerId === action?.payload?.callerId,
      )
      const copyCallSlots = [...state.callSlots]
      if (index !== -1) {
        copyCallSlots.splice(index, 1, {
          caller: `Empty slot ${index + 1}`,
          participants: [],
          isOnHold: false,
        })
      } else {
        const freeSpotIndex = state.callSlots.findIndex(
          (call) => !Object.prototype.hasOwnProperty.call(call, 'callerId'),
        )
        if (freeSpotIndex !== -1) {
          copyCallSlots.splice(freeSpotIndex, 1, action.payload)
        }
      }
      state.callSlots = copyCallSlots
    },
    removeCallSlot: (state, action) => {
      state.callSlots = state.callSlots.map((call, index) => {
        const callerIds = call?.callerId?.split('#') || []
        if (
          (callerIds?.length === 1 && callerIds[0] === action?.payload?.callerId) ||
          call?.callerId === action.payload?.callerId
        ) {
          return { caller: `Empty slot ${index + 1}`, participants: [], isOnHold: false }
        } else {
          const isInMergeCall = callerIds.find((id) => id === action?.payload?.callerId)
          if (isInMergeCall && !action?.payload.action) {
            return {
              ...call,
              callerId: callerIds.filter((id) => id !== action?.payload?.callerId).join('#'),
              participants: call.participants?.filter(
                (participant) => participant?.callerId !== action?.payload.callerId,
              ),
            }
          }
        }
        return call
      })
    },
    updateCurrentCall: (state, action) => {
      if (state?.currentCall) {
        const currentCal = { ...state.currentCall }
        const callerIds = currentCal?.callerId?.split('#') || []
        if (callerIds?.length === 1 && callerIds[0] === action?.payload?.callerId) {
          state.currentCall = null
        } else {
          state.currentCall = {
            ...currentCal,
            callerId: callerIds.filter((id) => id !== action?.payload?.callerId).join('#'),
            participants: currentCal.participants?.filter(
              (participant) => participant?.callerId !== action?.payload.callerId,
            ),
          }
        }
      }
    },
    setIsConference: (state, action) => {
      state.isConference = action.payload
    },
    setIsInVideoCall: (state, action) => {
      state.isInVideoCall = action.payload
    },
    replaceCallInSlot: (state, action) => {
      const copyCallSlots = [...state.callSlots]
      copyCallSlots.splice(action.payload.index, 1, { ...action.payload.call })
      state.callSlots = copyCallSlots
    },
    setIsOnHold: (state, action) => {
      state.isConference = true
      state.isOnHold = action.payload
    },
    setIsLocalHold: (state, action) => {
      state.isLocalHold = action.payload
    },
    setCurrentCallSid: (state, action) => {
      if (state.currentCall) state.currentCall.callSid = action.payload
      const active = state.localCalls.find((call) => call.id === state.activeLocalCallId)
      if (active) active.callSid = action.payload
    },
    setCallStartedAt: (state, action) => {
      state.callStartedAt = action.payload
      const active = state.localCalls.find((call) => call.id === state.activeLocalCallId)
      if (active) active.startedAt = action.payload
    },
    addLocalCall: (state, action) => {
      state.localCalls = [
        ...state.localCalls.filter((call) => call.id !== action.payload.id),
        { isOnHold: false, startedAt: null, ...action.payload },
      ]
    },
    updateLocalCall: (state, action) => {
      const { id, ...patch } = action.payload
      const call = state.localCalls.find((call) => call.id === id)
      if (call) Object.assign(call, patch)
    },
    removeLocalCall: (state, action) => {
      state.localCalls = state.localCalls.filter((call) => call.id !== action.payload)
      if (state.activeLocalCallId === action.payload) state.activeLocalCallId = null
    },
    // Marks one local call active and every other one on hold; restores currentCall + timer for it
    setActiveLocalCall: (state, action) => {
      state.activeLocalCallId = action.payload
      state.localCalls.forEach((call) => {
        call.isOnHold = call.id !== action.payload
      })
      const active = state.localCalls.find((call) => call.id === action.payload)
      if (active) {
        state.currentCall = {
          caller: active.caller,
          callerId: active.callerId,
          organizationName: active.organizationName,
          callSid: active.callSid,
          incomingCallSid: active.incomingCallSid,
          direction: active.direction,
          isOnHold: false,
        }
        state.callStartedAt = active.startedAt
        state.isLocalHold = false
      }
    },
  },
})

export const {
  setCallsOnHold,
  setIsInCall,
  removeCallOnHold,
  setCurrentCall,
  resetCall,
  setCallSlot,
  setIsConference,
  removeCallSlot,
  setIsInVideoCall,
  updateCurrentCall,
  replaceCallInSlot,
  setIsOnHold,
  setIsLocalHold,
  setCurrentCallSid,
  setCallStartedAt,
  addLocalCall,
  updateLocalCall,
  removeLocalCall,
  setActiveLocalCall,
} = callsSlice.actions

export default callsSlice.reducer

export const selectCallSlot = (state) => state.calls.callSlots
export const selectCallsOnHold = createSelector([selectCallSlot], (callSlots) =>
  callSlots.filter((call) => call?.isOnHold),
)
export const selectIsLocalHold = (state) => state.calls.isLocalHold
export const selectCallStartedAt = (state) => state.calls.callStartedAt
export const selectLocalCalls = (state) => state.calls.localCalls
export const selectActiveLocalCallId = (state) => state.calls.activeLocalCallId
export const selectHeldLocalCalls = createSelector([selectLocalCalls], (localCalls) =>
  localCalls.filter((call) => call.isOnHold),
)
export const selectIsInCall = (state) => state.calls.isInCall
export const selectCurrentCall = (state) => state.calls.currentCall
export const selectIsConference = (state) => state.calls.isConference
export const selectIsInVideoCall = (state) => state.calls.isInVideoCall
export const selectIsOnHold = (state) => state.calls.isOnHold
